import React, { useMemo } from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { OrgChartRow, getDirectReports } from '../../../support/orgScope';
import type { PovActivityCounts } from '../../../hooks/useSfTeamPovActivitySummary';

type RowModel = {
  id: string;
  email: string;
  is_manager: boolean;
  /** As shown: the count, "…" while loading, or "—" when the server could not supply it. */
  opportunities: string;
  opps_with_pov_activity: string;
  pov_se_activities: string;
};

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  /**
   * Opportunity / POV-activity counts per direct report (lower-cased email), from
   * `sf/team/pov-activity-summary/`; a manager's figures are their team's rollup. Null when unavailable, so
   * the columns show "—", not a false 0.
   */
  activityByEmail?: Map<string, PovActivityCounts> | null;
  activityLoading?: boolean;
  onSelectEmail: (email: string) => void;
}

function safeIdFromEmail(email: string) {
  return `u_${email.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`;
}

export default function PovTeamMembersTable({
                                              orgRows,
                                              managerEmail,
                                              activityByEmail = null,
                                              activityLoading = false,
                                              onSelectEmail,
                                            }: Props) {
  const normalizedManagerEmail = String(managerEmail || '').trim().toLowerCase();

  const reports = useMemo(
    () => getDirectReports(orgRows, normalizedManagerEmail),
    [orgRows, normalizedManagerEmail]
  );

  const data: RowModel[] = useMemo(() => {
    return reports.map((r) => {
      const email = String(r.user_email || '').trim().toLowerCase();
      const act = activityByEmail?.get(email);
      const show = (n: number | undefined) => (activityLoading ? '…' : n == null ? '—' : String(n));

      return {
        id: safeIdFromEmail(email),
        email,
        is_manager: !!r.is_manager,
        opportunities: show(act?.opportunity_count),
        opps_with_pov_activity: show(act?.opps_with_pov_activity_count),
        pov_se_activities: show(act?.pov_activity_count),
      };
    });
  }, [reports, activityByEmail, activityLoading]);

  const columns: Column[] = useMemo(() => {
    // Percentage widths (summing to 100%) rather than fixed px: HbrDomTable's table is width:100%, so
    // percent columns shrink/grow WITH the container and never force horizontal scroll. Fixed px widths
    // become hard min-widths (see column.service setWidth) whose sum overflowed the screen.
    const base: Column[] = [
      {
        id: 'email',
        name: 'Team Member',
        width: '40%',
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Left,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      },
      {
        // Open opportunities closing in the selected period (a manager's: their whole team's), from Salesforce.
        id: 'opportunities',
        name: 'Opportunities',
        width: '20%',
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Right,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      },
      {
        // Of those, how many have at least one POC/POV SE activity (logged on any date).
        id: 'opps_with_pov_activity',
        name: 'Opps w/ POV Activity',
        width: '20%',
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Right,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      },
      {
        // POC/POV SE activities on those opportunities.
        id: 'pov_se_activities',
        name: 'POV SE Activities',
        width: '20%',
        columnType: ColumnType.Custom,
        sortable: false,
        align: ColumnAlignment.Right,
        dataType: DataType.String,
        resizable: false,
        reorderable: false,
      },
    ];

    return base
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const renderHeader = () =>
    // No `w-max` here: that forced each header to its single-line max-content width, expanding the
    // column past its percentage share. Let the label wrap/ellipsize within the allotted width instead.
    columns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center relative"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    if (!data.length) return null;

    const list: JSX.Element[] = [];

    for (const row of data) {
      for (const column of columns) {
        const slot = `row-${row.id}-column-${column.id}`;

        if (column.id === 'email') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 min-w-0 flex items-center font-semibold hover:text-link hover:underline cursor-pointer truncate"
              title={`${row.email}${row.is_manager ? ' (Mgr)' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectEmail(row.email);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectEmail(row.email);
                }
              }}
              aria-label={`View ${row.email}`}
            >
              {row.email}
              {row.is_manager ? ' (Mgr)' : ''}
            </div>
          );
          continue;
        }

        const value = (row as any)[column.id];
        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
          >
            {value ?? 0}
          </div>
        );
      }
    }

    return list;
  };

  if (!reports.length) {
    return <div className="hbr-css__text-secondary-sm">No direct reports found.</div>;
  }

  return (
    <HbrDomTable
      autoLayout={true}
      columns={columns}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}